import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Image as ImageIcon, X, AlertCircle, FileImage } from 'lucide-react';
import { validateFile, formatFileSize, createImagePreview, revokeImagePreview } from '../../utils/fileValidation';
import { FILE_VALIDATION } from '../../lib/constants';

const ImageUploader = ({ onFileSelect, disabled = false }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError(null);

    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        setError('File is too large. Maximum size is 5MB.');
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        setError('Invalid file type. Please upload JPG, PNG, or WEBP images.');
      } else {
        setError('Invalid file. Please try again.');
      }
      return;
    }

    // Handle accepted files
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // Validate file
      const validation = validateFile(file);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }

      // Clean up previous preview
      if (previewUrl) {
        revokeImagePreview(previewUrl);
      }

      // Set new file and preview
      setSelectedFile(file);
      const preview = createImagePreview(file);
      setPreviewUrl(preview);
      setError(null);

      // Notify parent component
      if (onFileSelect) {
        onFileSelect(file);
      }
    }
  }, [previewUrl, onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    maxSize: FILE_VALIDATION.MAX_SIZE_BYTES,
    maxFiles: 1,
    disabled,
  });

  const removeFile = () => {
    if (previewUrl) {
      revokeImagePreview(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    
    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  // Clean up preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        revokeImagePreview(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div className="w-full">
      {!selectedFile ? (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 lg:p-12 text-center transition-all ${
            isDragActive
              ? 'border-brown-500 bg-brown-50/50 backdrop-blur-sm'
              : 'border-cream-300 hover:border-brown-400 hover:bg-cream-50/30'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <input {...getInputProps()} />
          
          <div className="flex flex-col items-center space-y-3 sm:space-y-4 md:space-y-5">
            {/* Icon - Responsive sizing */}
            <div className={`p-3 sm:p-4 md:p-5 rounded-full transition-all ${
              isDragActive ? 'bg-brown-100' : 'bg-cream-100'
            }`}>
              {isDragActive ? (
                <FileImage className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 text-brown-600" />
              ) : (
                <Upload className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 text-brown-400" />
              )}
            </div>

            {/* Text - Responsive sizing */}
            <div className="space-y-1 sm:space-y-2">
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold text-brown-900">
                {isDragActive ? 'Drop your image here' : 'Upload textbook page'}
              </p>
              <p className="text-xs sm:text-sm md:text-base text-brown-600 px-2">
                Drag and drop or click to browse
              </p>
            </div>

            {/* File type badges - Responsive */}
            <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
              <span className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-cream-100/80 text-brown-700 rounded-full font-medium border border-cream-200">
                JPG
              </span>
              <span className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-cream-100/80 text-brown-700 rounded-full font-medium border border-cream-200">
                PNG
              </span>
              <span className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-cream-100/80 text-brown-700 rounded-full font-medium border border-cream-200">
                WEBP
              </span>
              <span className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-brown-100/80 text-brown-800 rounded-full font-medium border border-brown-200">
                Max 5MB
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="backdrop-blur-sm bg-white/40 border-2 border-cream-300 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 md:space-x-5">
            
            {/* Image Preview - Responsive sizing */}
            <div className="flex-shrink-0 w-full sm:w-auto">
              <div className="w-full h-48 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 bg-cream-100 rounded-lg sm:rounded-xl overflow-hidden border-2 border-cream-200 shadow-md">
                {previewUrl ? (
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-10 h-10 sm:w-8 sm:h-8 text-cream-400" />
                  </div>
                )}
              </div>
            </div>

            {/* File Info - Responsive text */}
            <div className="flex-1 min-w-0 w-full sm:w-auto">
              <p className="text-sm sm:text-base md:text-lg font-semibold text-brown-900 truncate mb-1 sm:mb-1.5">
                {selectedFile.name}
              </p>
              <p className="text-xs sm:text-sm text-brown-600 mb-2 sm:mb-3">
                {formatFileSize(selectedFile.size)}
              </p>
              <div>
                <span className="inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-green-100/60 text-green-700 border border-green-200/50">
                  ✓ Ready to upload
                </span>
              </div>
            </div>

            {/* Remove Button - Responsive positioning */}
            <button
              onClick={removeFile}
              disabled={disabled}
              className="flex-shrink-0 p-2 sm:p-2.5 text-brown-400 hover:text-red-600 hover:bg-red-50/50 rounded-lg transition-all disabled:opacity-50 self-end sm:self-auto"
              title="Remove file"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>
      )}

      {/* Error Message - Responsive */}
      {error && (
        <div className="mt-3 sm:mt-4 flex items-start space-x-2 sm:space-x-3 p-3 sm:p-4 bg-red-50/50 border border-red-200/50 rounded-lg sm:rounded-xl backdrop-blur-sm animate-slide-up">
          <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm md:text-base text-red-800 leading-relaxed">{error}</p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;